<script setup lang="ts">
import {
  computed, inject, nextTick, onBeforeUnmount, onMounted, watch,
} from 'vue';
import { CdrModal, CdrText } from '@rei/cedar';
import useArticleContent from '../../composables/useArticleContent';
import type { centerState } from '../../models/types/ColumnsCenterState';

const injected = inject<centerState>('centerState');

if (!injected) {
  throw new Error('MobileArticleModal requires a centerState provider');
}

const { mainState, setState } = injected;

const {
  articleContent, isLoading, error, reset,
} = useArticleContent();

const articleId = computed(() => mainState.value.articleId);

// Should match the parent's mobile breakpoint
const DESKTOP_MEDIA_QUERY = '(min-width: 768px)';
let mediaQueryList: MediaQueryList | null = null;

// ─── Qualtrics "Was this helpful?" widget integration ─────────────────
// The widget is rendered by Qualtrics into the FIRST `.help-center-feedback`
// container it finds in the DOM. That container lives in GladlySearch.vue
// and is hidden by default (display: none) by gladlyConditionalElements.
//
// Strategy: when the modal opens, move that legacy container into the modal
// body and force it visible. When the modal closes, return it to its
// original location. This avoids having two containers with the same id
// (invalid HTML) and lets the existing native article flow keep working.
let legacyFeedback: HTMLElement | null = null;
let legacyOriginalParent: Node | null = null;
let legacyOriginalNextSibling: Node | null = null;

const captureLegacyFeedback = (): void => {
  if (legacyFeedback) return;
  const candidate = globalThis.document.querySelector<HTMLElement>(
    '.help-center-feedback',
  );
  if (!candidate) return;
  legacyFeedback = candidate;
  legacyOriginalParent = candidate.parentNode;
  legacyOriginalNextSibling = candidate.nextSibling;
};

const moveLegacyIntoModal = async (): Promise<void> => {
  await nextTick();
  // Wait one extra frame so CdrModal's teleport is committed before we query.
  await new Promise<void>((r) => { globalThis.requestAnimationFrame(() => r()); });

  captureLegacyFeedback();
  if (!legacyFeedback) return;

  const modalBody = globalThis.document.querySelector(
    '[data-ui="mobile-article-modal-body"]',
  );
  if (!modalBody) return;

  legacyFeedback.style.setProperty('display', 'block', 'important');
  modalBody.appendChild(legacyFeedback);

  // Defensive: if Qualtrics hasn't injected the widget yet, prompt it.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const qsi = (globalThis.window as any)?.QSI?.API;
  if (qsi) {
    try {
      qsi.unload();
      qsi.load();
      qsi.run();
    } catch {
      // ignore — widget may already be rendered from page-load
    }
  }
};

const restoreLegacyFeedback = (): void => {
  if (!legacyFeedback || !legacyOriginalParent) return;
  legacyFeedback.style.setProperty('display', 'none');
  if (
    legacyOriginalNextSibling
    && legacyOriginalNextSibling.parentNode === legacyOriginalParent
  ) {
    legacyOriginalParent.insertBefore(legacyFeedback, legacyOriginalNextSibling);
  } else {
    legacyOriginalParent.appendChild(legacyFeedback);
  }
};
// ──────────────────────────────────────────────────────────────────────

const handlePopstate = (): void => {
  setState({ articleId: '' });
};

const handleClose = (): void => {
  setState({ articleId: '' });
};

const handleViewportChange = (event: MediaQueryListEvent): void => {
  if (event.matches && articleId.value) {
    setState({ articleId: '' });
  }
};

// history.state makes the watch idempotent; prevId skips the immediate run
watch(
  articleId,
  (id, prevId) => {
    if (typeof globalThis.window === 'undefined') return;

    if (id) {
      if (!globalThis.history.state?.articleModalOpen) {
        globalThis.history.pushState({ articleModalOpen: true }, '');
      }
      globalThis.addEventListener('popstate', handlePopstate);
      moveLegacyIntoModal();
      return;
    }

    if (prevId) {
      globalThis.removeEventListener('popstate', handlePopstate);
      if (globalThis.history.state?.articleModalOpen) {
        globalThis.history.back();
      }
      restoreLegacyFeedback();
      reset();
    }
  },
  { immediate: true },
);

onMounted(() => {
  if (typeof globalThis.window === 'undefined') return;
  mediaQueryList = globalThis.matchMedia(DESKTOP_MEDIA_QUERY);
  mediaQueryList.addEventListener('change', handleViewportChange);
});

onBeforeUnmount(() => {
  if (typeof globalThis.window !== 'undefined') {
    globalThis.removeEventListener('popstate', handlePopstate);
    mediaQueryList?.removeEventListener('change', handleViewportChange);
  }
  restoreLegacyFeedback();
  reset();
});
</script>

<template>
  <CdrModal
    class="mobile-article-modal"
    data-backstop="modal"
    data-ui="mobile-article-modal"
    role="dialog"
    content-padding="one-and-a-half-x"
    :label="articleContent?.name || ''"
    :opened="Boolean(articleId)"
    :show-title="Boolean(articleContent?.name)"
    @closed="handleClose"
  >
    <template #title>
      <CdrText
        v-if="articleContent?.name"
        tabindex="0"
        tag="h2"
        class="mobile-article-modal__title"
        data-ui="mobile-article-modal-title"
      >
        {{ articleContent.name }}
      </CdrText>
    </template>

    <div
      class="mobile-article-modal__body"
      data-ui="mobile-article-modal-body"
    >
      <div
        v-if="isLoading"
        class="mobile-article-modal__loading"
        data-ui="mobile-article-modal-loading"
      >
        Loading…
      </div>

      <CdrText
        v-else-if="error"
        class="mobile-article-modal__error"
        data-ui="mobile-article-modal-error"
      >
        Sorry, we couldn't load this article. Please try again.
      </CdrText>

      <div
        v-else-if="articleContent?.bodyHtml"
        class="mobile-article-modal__content"
        v-html="articleContent.bodyHtml"
      />
    </div>
  </CdrModal>
</template>

<style lang="scss">
@import 'src/main/style/helpCenterColumnsUpdate.scss';

.mobile-article-modal {
  &__title {
    @include cdr-text-heading-serif-600;

    @include cdr-xs-mq-only {
      @include cdr-text-heading-serif-500;
    }
  }

  &__body {
    @include cdr-text-body-300;

    display: grid;
    grid-gap: $cdr-space-one-x;
    padding: $cdr-space-eighth-x;
  }

  &__loading,
  &__error {
    padding: $cdr-space-two-x;
    text-align: center;
  }
}
</style>
